import { useRef, useState } from 'react';
import CustomDayPicker from './CustomDayPicker';
import classNames from 'classnames';
import cleanFieldProps from '../utilities/cleanFieldProps';
import describeField from '../utilities/describeField';
import { isMatch } from 'date-fns';
import mergeIds from '../utilities/mergeIds';
import useLabelMask from '../TextField/useLabelMask';
import useClickOutsideHandler from '../utilities/useClickOutsideHandler';
import usePressEscapeHandler from '../utilities/usePressEscapeHandler';
import useId from '../utilities/useId';
import { CalendarIcon } from '../Icons/CalendarIcon';
import { createDateMask } from '../TextField/useLabelMask';
import { Label } from '../Label';
import { TextInput } from '../TextField';
import { getLanguage, t } from '../i18n';
import { useLabelProps } from '../Label/useLabelProps';
import { useHint } from '../Hint/useHint';
import { useInlineError } from '../InlineError/useInlineError';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * For information about how and when to use this component, refer to the
 * [Single Input Date Field](https://design.cms.gov/components/date-field/single-input-date-field/) and
 * [Calendar Picker](https://design.cms.gov/components/date-field/date-picker/) documentation pages.
 */
const SingleInputDateField = props => {
  const {
    className,
    onBlur,
    onChange,
    defaultMonth,
    fromDate,
    fromMonth,
    fromYear,
    toDate,
    toMonth,
    toYear,
    ...remainingProps
  } = props;
  const withPicker = (fromDate != null || fromMonth != null || Number.isInteger(fromYear)) && (toDate != null || toMonth != null || Number.isInteger(toYear));
  const [pickerVisible, setPickerVisible] = useState(false);
  const id = useId('date-field--', props.id);
  const isControlled = remainingProps.value !== undefined;
  const [internalValueState, setInternalValueState] = useState(remainingProps.defaultValue);
  const value = isControlled ? remainingProps.value : internalValueState;
  // This currently just supports English and non-english date formatting. Maybe could expand later.
  const lang = getLanguage();
  const localizedDateHint = lang === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  const dateMask = createDateMask(localizedDateHint);
  const destructureDate = dateString => {
    const dateParts = dateString.split('/');
    const month = lang === 'en' ? parseInt(dateParts[0]) - 1 : parseInt(dateParts[1]) - 1;
    const day = lang === 'en' ? parseInt(dateParts[1]) : parseInt(dateParts[0]);
    const year = parseInt(dateParts[2]);
    return [month, day, year];
  };
  const getValidDate = dateString => {
    const formattedDate = dateMask(dateString, true);
    const testValue = lang === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
    if (!isMatch(formattedDate, testValue)) {
      return undefined;
    }
    const [month, day, year] = destructureDate(formattedDate);

    // Years must be 4 digits long.
    // Fun fact: new Date(202) = Year 0202, which is totally reasonable.
    if (String(year).length < 4) return undefined;
    const date = new Date(year, month, day);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
      return undefined;
    }
    return date;
  };

  // Set up change handlers
  const handleInputChange = event => {
    const updatedValue = event.currentTarget.value;
    const formattedValue = dateMask(updatedValue, true);
    const date = getValidDate(updatedValue);
    if (onChange) {
      onChange(updatedValue, formattedValue, date);
    }
    if (!isControlled) {
      setInternalValueState(updatedValue);
    }
  };
  const computeDateValue = (date, lang) => {
    const vals = [date.getMonth() + 1, date.getDate(), date.getFullYear()];
    return lang === 'en' ? `${vals[0]}/${vals[1]}/${vals[2]}` : `${vals[1]}/${vals[0]}/${vals[2]}`;
  };
  const handleBlur = event => {
    const updatedValue = event.currentTarget.value;
    const date = getValidDate(updatedValue);
    if (onBlur) {
      onBlur(event, date);
    }
  };
  const handlePickerChange = date => {
    if (!date) {
      setPickerVisible(false);
      inputRef.current?.focus();
      return;
    }
    const updatedValue = computeDateValue(date, lang);
    const maskedValue = dateMask(updatedValue);
    const validDate = getValidDate(updatedValue);
    if (onChange) {
      onChange(maskedValue, dateMask(updatedValue, true), validDate);
    }
    if (!isControlled) {
      setInternalValueState(maskedValue);
    }
    setPickerVisible(false);
    inputRef.current?.focus();
  };

  // Collect all the props and elements for the input and its labels
  const {
    errorId,
    topError,
    bottomError,
    invalid
  } = useInlineError({
    ...props,
    id
  });
  const {
    hintId,
    hintElement
  } = useHint({
    ...props,
    id
  });
  const labelProps = useLabelProps({
    ...props,
    id
  });
  const inputRef = useRef(null);
  const {
    labelMask,
    inputProps
  } = useLabelMask(dateMask, {
    ...cleanFieldProps(remainingProps),
    value,
    id,
    onBlur: handleBlur,
    onChange: handleInputChange,
    type: 'text',
    inputRef,
    'aria-invalid': invalid,
    'aria-describedby': describeField({
      ...props,
      errorId,
      hintId
    })
  });

  // Handle alternate ways of closing the day picker
  const dayPickerRef = useRef(null);
  const calendarButtonRef = useRef(null);
  useClickOutsideHandler([dayPickerRef, calendarButtonRef], () => setPickerVisible(false));
  usePressEscapeHandler(dayPickerRef, () => {
    setPickerVisible(false);
    inputRef.current?.focus();
  });
  const handleDayPickerKeyDown = event => {
    if (event.key !== 'Tab') return;
    const focusableElements = Array.from(event.currentTarget.querySelectorAll('button:not([disabled]), select:not([disabled])')).filter(element => element.tabIndex >= 0);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = event.currentTarget.ownerDocument.activeElement;
    if (!firstElement || !lastElement || !activeElement) return;
    if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  // Validate the date string (value) and make date null if it's invalid. We don't want to pass
  // a bizarre date to DayPicker like `new Date('01/02')`, which is interpreted as `Jan 02, 2001`
  const dateString = dateMask(value ?? '', true);
  const date = getValidDate(dateString);
  return /*#__PURE__*/_jsxs("div", {
    className: classNames('ds-c-single-input-date-field', withPicker && 'ds-c-single-input-date-field--with-picker', className),
    children: [/*#__PURE__*/_jsx(Label, {
      ...labelProps,
      fieldId: id
    }), hintElement, topError, labelMask, /*#__PURE__*/_jsxs("div", {
      className: "ds-c-single-input-date-field__field-wrapper",
      children: [/*#__PURE__*/_jsx(TextInput, {
        ...inputProps
      }), withPicker && /*#__PURE__*/_jsx("button", {
        className: "ds-c-single-input-date-field__button",
        onClick: () => setPickerVisible(!pickerVisible),
        type: "button",
        ref: calendarButtonRef,
        "aria-describedby": mergeIds(labelProps.id, hintId),
        children: /*#__PURE__*/_jsx(CalendarIcon, {
          ariaHidden: false,
          title: t(pickerVisible ? 'singleInputDateField.close' : 'singleInputDateField.open'),
          id: `${id}__icon`
        })
      })]
    }), pickerVisible && /*#__PURE__*/_jsx("div", {
      ref: dayPickerRef,
      role: "dialog",
      onKeyDown: handleDayPickerKeyDown,
      children: /*#__PURE__*/_jsx(CustomDayPicker, {
        selected: date,
        required: true,
        onSelect: handlePickerChange,
        defaultMonth: date ?? defaultMonth,
        fromDate,
        fromMonth,
        fromYear,
        toDate,
        toMonth,
        toYear
      })
    }), bottomError]
  });
};
export default SingleInputDateField;